import { X } from "lucide-react";
import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { DatasetPicker } from "@/shared/components/pickers";
import { Field, RobotoButton, TagInput } from "@/shared/foundation/primitives";

import {
  INPUT_SOURCE_COPY,
  triggeringDatasetCopy,
  type TriggeringSubject,
} from "./inputSourceCopy";
import styles from "./invocationSpec.module.css";
import { type InputSourceValues } from "./invocationSpecSchema";
import { RoboqlQueryField } from "./RoboqlQueryField";

export interface InputSourceRowProps {
  /** Where this row lives, e.g. `targets.0.spec.inputs.2`. */
  name: string;
  index: number;
  onRemove: () => void;
  /** What the firing is about, for the row that mounts the dataset it carries. */
  triggeringSubject?: TriggeringSubject;
}

/**
 * One source of inputs beyond whatever the invocation is already handed: a
 * dataset with optional file patterns, the dataset the trigger fired on, or a
 * query resolved when it runs.
 */
export function InputSourceRow({
  name,
  index,
  onRemove,
  triggeringSubject,
}: InputSourceRowProps) {
  const { control } = useFormContext();
  const source = useWatch({
    control,
    name: `${name}.source`,
  }) as InputSourceValues["source"];
  const titleId = React.useId();
  const copy =
    source === "triggering-dataset" && triggeringSubject !== undefined
      ? triggeringDatasetCopy(triggeringSubject)
      : INPUT_SOURCE_COPY[source];

  // Both dataset rows narrow what they mount the same way.
  const globs = (
    <Controller
      control={control}
      name={`${name}.globs`}
      render={({ field }) => (
        <Field.Root>
          <Field.Label>File patterns</Field.Label>
          <TagInput
            tags={Array.isArray(field.value) ? field.value : []}
            onChange={field.onChange}
            placeholder="**/*.mcap"
            aria-label="File patterns"
            size="lg"
          />
          <Field.Description>
            Add several with Enter or a comma. With none, every file in the
            dataset is mounted.
          </Field.Description>
        </Field.Root>
      )}
    />
  );

  return (
    <div
      className={styles.row}
      data-testid={`input-source-${index}`}
      data-source={source}
      data-subject={
        source === "triggering-dataset" ? triggeringSubject : undefined
      }
    >
      <section className={styles.contents} aria-labelledby={titleId}>
        <header className={styles.heading}>
          <h5 id={titleId} className={styles.rowTitle}>
            {copy.title}
          </h5>
          <p className={styles.hint}>{copy.description}</p>
        </header>
        {source === "dataset" && (
          <div className={styles.grid}>
            <Controller
              control={control}
              name={`${name}.datasetId`}
              render={({ field, fieldState }) => (
                <Field.Root invalid={fieldState.invalid}>
                  <DatasetPicker
                    value={typeof field.value === "string" ? field.value : null}
                    onValueChange={field.onChange}
                    label="Dataset"
                  />
                  <Field.Error match>{fieldState.error?.message}</Field.Error>
                </Field.Root>
              )}
            />
            {globs}
          </div>
        )}
        {/* The dataset is the firing's own, so there is nothing to pick. */}
        {source === "triggering-dataset" && globs}
        {source === "query" && <RoboqlQueryField name={`${name}.text`} />}
      </section>

      <RobotoButton
        variant="ghost"
        icon={<X aria-hidden size={16} />}
        aria-label="Remove this input"
        onClick={onRemove}
        data-testid={`input-source-${index}-remove`}
      />
    </div>
  );
}
